import { CrossUI } from 'phx-core';

import { Component, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Session } from '@services/session';

import { ANY_INDUSTRY_KEY, CTA_ACTION_TYPES, EXPANSION_UPSELL_TYPES, ExpansionUpsellService } from '../../service';
import { ExpansionUpsellConfig, IExpansionUpsellsData, IExpansionUpsellsFloatingUpsell, IUpsellCTA } from '../../interface';

type UpsellType = keyof typeof EXPANSION_UPSELL_TYPES;

@Component({
  selector: 'app-expansion-upsells-floating-upsell',
  templateUrl: './floating-upsell.component.html',
  styleUrls: ['./floating-upsell.component.scss'],
})
export class ExpansionUpsellsFloatingUpsellComponent {
  public isVisible: boolean = false;

  public readonly crossIcon: string = CrossUI;
  public readonly upsellType: UpsellType = EXPANSION_UPSELL_TYPES.FLOATING as UpsellType;

  public showModal: boolean = false;
  public CTAs: IUpsellCTA[] = [];
  public industry: string;
  public assetURL: string;
  public assetTransform: number;
  public assetMarginTop: string;
  public header: SafeHtml;

  public modalHeader: SafeHtml;
  public modalBody: SafeHtml;
  public modalAssetURL: string;
  public modalCTAs: IUpsellCTA[] = [];

  constructor(
    private service: ExpansionUpsellService,
    private sanitzer: DomSanitizer,
    private session: Session,
  ) { }

  ngOnInit(): void {
    if (this.session.isDriver() || !this.service.isFeatureFlagEnabled()) return;
    this.service.getIndustry().subscribe((segments: string[]) => {
      segments.push(ANY_INDUSTRY_KEY);
      const config: IExpansionUpsellsData = this.service.getDynamicConfig();
      const upsellIndustries: Set<string> = new Set(config.upsells.filter(
        (conf: ExpansionUpsellConfig) => conf.type === this.upsellType,
      ).map((conf: ExpansionUpsellConfig) => conf.industry));

      const matchingIndustries: string[] = segments.filter(segment => upsellIndustries.has(segment));

      if (matchingIndustries.length === 1) {
        this.industry = matchingIndustries[0];
        if (this.service.shouldShowAGUpsell(this.upsellType, this.industry)) {
          const upsellConfig: IExpansionUpsellsFloatingUpsell = this.service.getUpsellConfig(
            this.upsellType,
            this.industry,
          ) as IExpansionUpsellsFloatingUpsell;
          if (upsellConfig) {
            this.setUpUpsell(upsellConfig);
          }
        }
      }
    });
  }

  private setUpUpsell(upsellConfig: IExpansionUpsellsFloatingUpsell): void {
    this.CTAs = upsellConfig.cta;

    this.assetURL = upsellConfig.image.url;
    this.assetTransform = upsellConfig.image.transform;
    this.assetMarginTop = upsellConfig.image['margin-top'];

    this.header = this.sanitzer.sanitize(
      SecurityContext.HTML,
      upsellConfig.heading[this.service.getUserLanguage()],
    );
    setTimeout(() => {
      this.isVisible = true;
      this.service.trackEvent(this.upsellType, this.industry, null, 'View');
      this.service.markUpsellAsShown(this.upsellType, this.industry);
    }, upsellConfig.display_after * 60 * 1000);
  }

  handleCTAClick(cta: IUpsellCTA): void {
    if (cta.action.type === CTA_ACTION_TYPES.modal) {
      this.modalHeader = this.sanitzer.sanitize(
        SecurityContext.HTML,
        cta.action.modal_config?.heading[this.service.getUserLanguage()],
      );
      this.modalBody = this.sanitzer.sanitize(
        SecurityContext.HTML,
        cta.action.modal_config?.description[this.service.getUserLanguage()],
      );
      this.modalAssetURL = cta.action.modal_config?.image.url;
      this.modalCTAs = cta.action.modal_config?.cta;

      this.showModal = true;
      this.isVisible = false;
      this.service.trackEvent(this.upsellType, this.industry, null, cta);
    }
  }

  public dismiss(): void {
    this.isVisible = false;
    this.service.markUpsellAsDismissed(this.upsellType, this.industry);
    this.service.trackEvent(this.upsellType, this.industry, null, 'Dismiss');
  }

  public dismissModal(): void {
    this.showModal = false;
    this.service.markUpsellAsDismissed(this.upsellType, this.industry);
    this.service.trackEvent(this.upsellType, this.industry, null, 'Dismiss');
  }

  public getCTAText(cta: IUpsellCTA): string {
    return cta.text[this.service.getUserLanguage()];
  }
}